/**
=========================================================
* Trust Cargo - pagination
=========================================================

* One pager for every list in the system.
*
* Client-side on purpose: the existing endpoints already return the whole
* array (users 113, orders 588, payments 653), so paging in the browser is
* instant, keeps search working across the FULL set rather than the current
* page, and needs no API change. If a table ever outgrows a few thousand
* rows, the hook is the single place to swap in server-side paging.
*/

import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

export const PAGE_SIZES = [10, 25, 50, 100];

/**
 * Slices `rows` into pages.
 *
 * The effect matters as much as the slicing: when a filter shrinks the list
 * while you are on page 7, staying there would show an empty table. Snapping
 * back into range is what stops "the search broke" reports.
 */
export function usePagination(rows, initialSize = 25) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialSize);

  const total = Array.isArray(rows) ? rows.length : 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  const pageRows = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, page, pageSize]);

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return {
    page,
    setPage,
    pageSize,
    setPageSize: (n) => {
      setPageSize(n);
      setPage(1);
    },
    pageRows,
    totalPages,
    total,
    from,
    to,
  };
}

/** page numbers with ellipses, so 24 pages do not render 24 buttons */
function pageList(page, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

  if (page <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
  if (page >= totalPages - 3)
    return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];

  return [1, "...", page - 1, page, page + 1, "...", totalPages];
}

function Pagination({ page, setPage, pageSize, setPageSize, totalPages, total, from, to }) {
  const btn =
    (active, disabled) =>
    ({ palette, functions: { rgba }, borders }) => ({
      minWidth: 34,
      height: 34,
      px: 1,
      display: "grid",
      placeItems: "center",
      borderRadius: borders.borderRadius.md,
      cursor: disabled ? "not-allowed" : "pointer",
      userSelect: "none",
      fontSize: 13,
      fontWeight: active ? 800 : 600,
      color: active ? palette.dark.main : rgba(palette.dark.main, disabled ? 0.28 : 0.66),
      backgroundColor: active ? rgba(palette.primary.main, 0.2) : "transparent",
      border: `1px solid ${rgba(palette.dark.main, active ? 0 : 0.1)}`,
      transition: "background-color 150ms ease, color 150ms ease",
      "&:hover": disabled
        ? {}
        : {
            backgroundColor: rgba(palette.primary.main, active ? 0.24 : 0.1),
            color: palette.dark.main,
          },
    });

  if (total === 0) return null;

  return (
    <SuiBox
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={1.5}
      px={3}
      py={2}
    >
      <SuiTypography
        variant="caption"
        sx={({ palette, functions: { rgba } }) => ({ color: rgba(palette.dark.main, 0.6) })}
      >
        {`Showing ${from}–${to} of ${total}`}
      </SuiTypography>

      <SuiBox display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
        <SuiBox sx={btn(false, page === 1)} onClick={() => page > 1 && setPage(page - 1)}>
          <Icon sx={{ fontSize: 18 }}>chevron_right</Icon>
        </SuiBox>

        {pageList(page, totalPages).map((p, i) =>
          p === "..." ? (
            // eslint-disable-next-line react/no-array-index-key
            <SuiBox key={`gap-${i}`} sx={{ px: 0.5, opacity: 0.5, fontSize: 13 }}>
              …
            </SuiBox>
          ) : (
            <SuiBox key={p} sx={btn(p === page, false)} onClick={() => setPage(p)}>
              {p}
            </SuiBox>
          )
        )}

        <SuiBox
          sx={btn(false, page === totalPages)}
          onClick={() => page < totalPages && setPage(page + 1)}
        >
          <Icon sx={{ fontSize: 18 }}>chevron_left</Icon>
        </SuiBox>
      </SuiBox>

      <SuiBox display="flex" alignItems="center" gap={1}>
        <SuiTypography
          variant="caption"
          sx={({ palette, functions: { rgba } }) => ({ color: rgba(palette.dark.main, 0.6) })}
        >
          per page
        </SuiTypography>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          style={{
            padding: "6px 10px",
            borderRadius: 8,
            border: "1px solid rgba(11,27,58,0.15)",
            fontFamily: "inherit",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {PAGE_SIZES.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </SuiBox>
    </SuiBox>
  );
}

Pagination.propTypes = {
  page: PropTypes.number.isRequired,
  setPage: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  setPageSize: PropTypes.func.isRequired,
  totalPages: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  from: PropTypes.number.isRequired,
  to: PropTypes.number.isRequired,
};

export default Pagination;
